import {
  Badge,
  Button,
  Link,
  Navbar,
  NavbarBrand,
  NavbarContent,
  NavbarItem,
  NavbarMenu,
  NavbarMenuItem,
  NavbarMenuToggle,
} from '@heroui/react';
import { Icon } from '@iconify/react';
import { useContext, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation } from 'react-router';

import Logo from '@/assets/images/seamlessMD-logo.png';
import { ActionCableContext } from '@/context/ActionCableContext';
import { NavDemo } from '@/components/demo/NavDemo';
import useHospitalFlags from '@/hooks/useHospitalFlags';
import { logout } from '@/patient-app-common/slices/authSlice';
import useAppEvents from '@/patient-app-common/hooks/useAppEvents';
import { useAppSelector } from '@/redux/hooks';

function NavBar({ handleSkip }: { handleSkip: () => void }) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { hospital } = useHospitalFlags();

  const { pathname } = useLocation();
  const { activeTodos, futureTodos, todoCount } = useAppEvents();
  const { inboxCount, secureMessageThreads } = useContext(ActionCableContext);

  const { ehrMode } = useAppSelector(({ webApp }) => webApp);
  const { procedureLogo } = useAppSelector(({ app }) => app);

  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const ITEMS = [
    {
      icon: <Icon icon="mdi:home-user" />,
      label: t('common.link.home'),
      link: '/home',
    },
    {
      count: todoCount,
      hidden: !activeTodos.length && !futureTodos.length,
      icon: <Icon icon="mdi:format-list-checks" />,
      label: t('common.link.todo'),
      link: '/todos',
    },
    {
      icon: <Icon icon="mdi:book-open-blank-variant" />,
      label: t('common.link.library'),
      link: '/education',
    },
    {
      icon: <Icon icon="mdi:trending-up" />,
      label: t('common.link.progress'),
      link: '/progress',
    },
    {
      count: inboxCount,
      hidden: !secureMessageThreads?.length,
      icon: <Icon icon="mdi:chat-processing-outline" />,
      label: t('common.link.inbox'),
      link: '/inbox',
    },
    {
      icon: <Icon icon="mdi:account-outline" />,
      label: t('common.link.profile'),
      link: '/profile',
    },
  ];

  const handleLogoutClick = () => {
    dispatch(logout());
  };

  return (
    <Navbar
      classNames={{
        item: [
          'flex',
          'relative',
          'h-full',
          'items-center',
          "data-[active=true]:after:content-['']",
          'data-[active=true]:after:absolute',
          'data-[active=true]:after:bottom-0',
          'data-[active=true]:after:left-0',
          'data-[active=true]:after:right-0',
          'data-[active=true]:after:h-[2px]',
          'data-[active=true]:after:rounded-[2px]',
          'data-[active=true]:after:bg-primary',
        ],
      }}
      isBordered
      isMenuOpen={isMenuOpen}
      onMenuOpenChange={setIsMenuOpen}
      shouldHideOnScroll
    >
      <Button
        className="transition -left-36 absolute -translate-y-16 focus:translate-y-0"
        onPress={handleSkip}
        size="sm"
        variant="light"
      >
        {t('common.link.skip')}
      </Button>
      <NavbarContent>
        <NavbarMenuToggle
          aria-label={t('common.layout.menu')}
          className="sm:hidden"
        />
        <NavbarBrand>
          <img
            className="bg-white rounded max-h-[60px] p-1"
            src={procedureLogo || hospital?.logo || Logo}
            alt={hospital?.displayName || 'SeamlessMD'}
          />
        </NavbarBrand>
      </NavbarContent>
      <NavbarContent className="hidden sm:flex gap-4" justify="center">
        {ITEMS.map(({ count, hidden, icon, label, link }) => {
          if (hidden) return;
          const isActive = pathname.includes(link);

          return (
            <NavbarItem
              key={link}
              isActive={isActive}
              aria-current={isActive ? 'page' : false}
              className="mx-0.5"
            >
              <Badge
                color="danger"
                content={count}
                isInvisible={!count}
                shape="rectangle"
              >
                <Link color={isActive ? 'primary' : 'foreground'} href={link}>
                  {icon}
                  <span className="ml-0.5">{label}</span>
                </Link>
              </Badge>
            </NavbarItem>
          );
        })}
      </NavbarContent>
      <NavbarContent justify="end">
        <NavDemo />
        <NavbarItem className={ehrMode ? 'hidden' : ''}>
          <Button
            as={Link}
            color="primary"
            onPress={handleLogoutClick}
            variant="flat"
          >
            {t('common.action.logout')}
          </Button>
        </NavbarItem>
      </NavbarContent>
      <NavbarMenu>
        {ITEMS.map(({ count, hidden, icon, label, link }) => {
          if (hidden) return;
          const isActive = pathname.includes(link);

          return (
            <NavbarMenuItem isActive={isActive} key={link}>
              <Badge
                color="danger"
                content={count}
                isInvisible={!count}
                shape="rectangle"
              >
                <Link
                  color={isActive ? 'primary' : 'foreground'}
                  className="w-full"
                  href={link}
                  onPress={() => setIsMenuOpen(false)}
                >
                  {icon}
                  <span className="ml-0.5">{label}</span>
                </Link>
              </Badge>
            </NavbarMenuItem>
          );
        })}
      </NavbarMenu>
    </Navbar>
  );
}

export function NavBarLayout() {
  const mainRef = useRef<HTMLDivElement>(null);

  const handleSkip = () => {
    const mainEl = mainRef.current;

    if (mainEl) {
      mainEl.focus({ preventScroll: true });
      const yOffset = 70; // px height of navbar in pixels
      const y = mainEl.getBoundingClientRect().top + window.scrollY - yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  return (
    <>
      <NavBar handleSkip={handleSkip} />
      <div ref={mainRef} tabIndex={-1} className="mx-4">
        <Outlet />
      </div>
    </>
  );
}
